import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { XCircle, AlertTriangle, Info } from 'lucide-react';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  showAlert: (options: AlertOptions) => Promise<boolean>;
}

interface AlertOptions {
  title: string;
  message: string;
  type?: 'info' | 'warning' | 'error';
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    options: AlertOptions;
    resolve?: (value: boolean) => void;
  }>({
    isOpen: false,
    options: { title: '', message: '' }
  });

  const addNotification = useCallback((notificationData: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification: Notification = {
      id,
      duration: 5000,
      persistent: false,
      ...notificationData
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove notification after duration (unless persistent)
    if (!notification.persistent && notification.duration) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showAlert = useCallback((options: AlertOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        options: {
          confirmText: 'OK',
          cancelText: 'Cancel',
          showCancel: false,
          type: 'info',
          ...options
        },
        resolve
      });
    });
  }, []);

  const handleAlertClose = useCallback((confirmed: boolean) => {
    if (alertState.resolve) {
      alertState.resolve(confirmed);
    }
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, [alertState.resolve]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showAlert
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Alert Dialog */}
      {alertState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                {alertState.options.type === 'error' && (
                  <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
                )}
                {alertState.options.type === 'warning' && (
                  <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                )}
                {alertState.options.type === 'info' && (
                  <Info className="h-6 w-6 text-blue-500 flex-shrink-0" />
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {alertState.options.title}
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 whitespace-pre-wrap">
                  {alertState.options.message}
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                {alertState.options.showCancel && (
                  <button
                    onClick={() => handleAlertClose(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {alertState.options.cancelText}
                  </button>
                )}
                
                <button
                  onClick={() => handleAlertClose(true)}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    alertState.options.type === 'error' 
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : alertState.options.type === 'warning'
                      ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  }`}
                >
                  {alertState.options.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;